import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TableTopExercise } from '../../types/tabletopExercise';

interface TabletopScenarioNotificationProps {
  isOpen: boolean;
  onClose: () => void;
  scenarios: TableTopExercise[];
  bcpName: string;
}

const TabletopScenarioNotification: React.FC<TabletopScenarioNotificationProps> = ({
  isOpen,
  onClose,
  scenarios,
  bcpName
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const displayBcp = bcpName && !bcpName.includes('null') && !bcpName.includes('undefined')
    ? bcpName
    : 'your organization';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                🎯 New ISO 27001 Tabletop Scenarios Available
              </h2>
              <p className="text-gray-600 mt-1">
                {scenarios.length} new cyber security tabletop exercise scenario{scenarios.length === 1 ? '' : 's'} {scenarios.length === 1 ? 'has' : 'have'} been generated for <strong>{displayBcp}</strong>
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scenarios List */}
          <div className="space-y-4 mb-6">
            {scenarios.map((scenario, index) => {
              const objectives = Array.isArray(scenario.response_objectives)
                ? scenario.response_objectives
                : [];
              const participants = Array.isArray(scenario.participants)
                ? scenario.participants
                : [];

              return (
                <div key={scenario.id ?? index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {index + 1}. {scenario.exercise_name}
                    </h3>
                    <div className="flex space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        scenario.status === 'generated'
                          ? 'bg-green-100 text-green-800'
                          : scenario.status === 'scheduled'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {scenario.status}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {scenario.exercise_type}
                      </span>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-3 line-clamp-3">
                    {scenario.scenario_description}
                  </p>

                  {/* Response Objectives */}
                  {objectives.length > 0 && (
                    <div className="mb-3">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">Learning Objectives:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {objectives.slice(0, 3).map((objective: any, objIndex: number) => {
                          const text = typeof objective === 'string'
                            ? objective
                            : (objective?.title || objective?.description || 'Objective');
                          return (
                            <li key={objIndex} className="flex items-start">
                              <span className="text-blue-500 mr-2">•</span>
                              <span>{text}</span>
                            </li>
                          );
                        })}
                        {objectives.length > 3 && (
                          <li className="text-gray-500 text-xs">
                            +{objectives.length - 3} more objectives
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {/* Key Participants */}
                  {participants.length > 0 && (
                    <div className="mb-3">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">Participants:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {participants.slice(0, 3).map((participant: any, pIndex: number) => {
                          const text = typeof participant === 'string'
                            ? participant
                            : participant && typeof participant === 'object'
                            ? [participant.name, participant.role].filter(Boolean).join(' - ') || participant.email || 'Participant'
                            : String(participant ?? '');
                          return (
                            <li key={pIndex} className="flex items-start">
                              <span className="text-blue-500 mr-2">•</span>
                              <span>{text}</span>
                            </li>
                          );
                        })}
                        {participants.length > 3 && (
                          <li className="text-gray-500 text-xs">
                            +{participants.length - 3} more participants
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  <div className="flex justify-between items-center text-sm text-gray-500">
                    <div className="flex space-x-4">
                      <span><strong>Type:</strong> {scenario.exercise_type}</span>
                      <span><strong>Status:</strong> {scenario.status}</span>
                    </div>
                    <span className="text-xs">
                      Generated {scenario.created_at ? new Date(scenario.created_at).toLocaleDateString() : 'recently'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              These scenarios are now available in your dashboard for scheduling and conducting exercises.
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  navigate('/tabletop-exercises');
                  onClose();
                }}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors"
              >
                View in Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabletopScenarioNotification;
